import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  SurveyResponseRow,
  SurveyRoleSummary,
  SurveySummary,
} from '../api/client';

/**
 * Answers to the pre-launch tester survey (staynow.live/sondage).
 *
 * Read-only: the summary on top is what to act on, the list underneath is for
 * the "why" behind it. Each new answer is also emailed to the team, so this
 * page is for the overview rather than for noticing new ones.
 */

type RoleFilter = 'all' | 'voyageur' | 'hote';

const ROLE_LABELS: Record<SurveyResponseRow['role'], string> = {
  voyageur: 'Voyageur',
  hote: 'Hôte',
};

const DATE_FORMAT = new Intl.DateTimeFormat('fr-CA', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function RoleSummaryCard({
  title,
  summary,
  likelihoodLabel,
  barriersLabel,
}: {
  title: string;
  summary: SurveyRoleSummary;
  likelihoodLabel: string;
  barriersLabel: string;
}) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="card-title">
        {title} · {summary.count} réponse{summary.count > 1 ? 's' : ''}
      </div>
      {summary.count === 0 ? (
        <p className="survey-muted">Aucune réponse pour l’instant.</p>
      ) : (
        <>
          <div className="survey-averages">
            <div>
              <div className="kpi-label">{likelihoodLabel}</div>
              <div className="kpi-value accent">
                {summary.likelihood_avg ?? '—'}
                <span className="survey-out-of"> / 5</span>
              </div>
            </div>
            <div>
              <div className="kpi-label">Clarté du prix qui baisse</div>
              <div className="kpi-value">
                {summary.price_clarity_avg ?? '—'}
                <span className="survey-out-of"> / 5</span>
              </div>
            </div>
          </div>
          <div className="kpi-label" style={{ marginTop: 16 }}>
            {barriersLabel}
          </div>
          <ul className="survey-bars">
            {summary.barriers.map((b) => (
              <li key={b.id}>
                <span className="survey-bar-label">{b.label}</span>
                <span className="survey-bar-track">
                  <span
                    className="survey-bar-fill"
                    style={{ width: `${(b.count / summary.count) * 100}%` }}
                  />
                </span>
                <span className="survey-bar-count">{b.count}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function toCsv(rows: SurveyResponseRow[], label: (id: string) => string) {
  const header = [
    'date',
    'role',
    'probabilite',
    'clarte_prix',
    'freins',
    'autre',
    'bloque_agace',
    'une_chose_a_changer',
    'a_reserve',
    'a_publie',
    'version',
    'recontact',
    'nom',
    'courriel',
  ];
  const cell = (value: unknown) =>
    `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [
      r.updated_at,
      ROLE_LABELS[r.role],
      r.answers.q1_likelihood,
      r.answers.q2_price_clarity,
      r.answers.q3_barriers.choices.map(label).join(' · '),
      r.answers.q3_barriers.other,
      r.answers.q4_friction,
      r.answers.q5_one_change,
      r.has_booked ? 'oui' : 'non',
      r.has_published ? 'oui' : 'non',
      r.app_version,
      r.contact_ok ? 'oui' : 'non',
      r.contact?.name,
      r.contact?.email,
    ]
      .map(cell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\n');
}

export default function Surveys() {
  const [rows, setRows] = useState<SurveyResponseRow[]>([]);
  const [summary, setSummary] = useState<SurveySummary | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RoleFilter>('all');

  const load = useCallback(async (next?: string) => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.getSurveyResponses(next);
      setRows((prev) => (next ? [...prev, ...page.data] : page.data));
      if (page.summary) setSummary(page.summary);
      setCursor(page.has_more ? page.next_cursor : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Every option anyone picked appears in the summary, so it doubles as the
  // id → label lookup for the list.
  const optionLabel = useMemo(() => {
    const labels = new Map<string, string>();
    for (const role of [summary?.voyageur, summary?.hote]) {
      role?.barriers.forEach((b) => labels.set(b.id, b.label));
    }
    return (id: string) => labels.get(id) ?? id;
  }, [summary]);

  const visible = rows.filter((r) => filter === 'all' || r.role === filter);

  function downloadCsv() {
    const blob = new Blob([`﻿${toCsv(visible, optionLabel)}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sondage-beta-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (loading && rows.length === 0) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (error && rows.length === 0) {
    return (
      <div className="empty">
        <div className="empty-title">Impossible de charger les réponses</div>
        <div className="empty-sub">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="section-head">
        <div>
          <div className="section-title">Sondage bêta</div>
          <div className="section-subtitle">
            {summary?.total ?? 0} réponse{(summary?.total ?? 0) > 1 ? 's' : ''}{' '}
            · {summary?.contact_ok ?? 0} acceptent d’être recontactés
          </div>
        </div>
      </div>

      {summary && summary.total > 0 && (
        <div className="survey-summary">
          <RoleSummaryCard
            title="Voyageurs"
            summary={summary.voyageur}
            likelihoodLabel="Probabilité de réutiliser"
            barriersLabel="Ce qui les empêcherait de réserver"
          />
          <RoleSummaryCard
            title="Hôtes"
            summary={summary.hote}
            likelihoodLabel="Probabilité de publier"
            barriersLabel="Ce qui les empêcherait de publier"
          />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">✉</div>
          <div className="empty-title">Aucune réponse pour l’instant</div>
          <div className="empty-sub">
            Les testeurs répondent depuis Profil → Donner mon avis dans l’app.
          </div>
        </div>
      ) : (
        <>
          <div className="section-head" style={{ marginTop: 24 }}>
            <div className="survey-filters">
              {(['all', 'voyageur', 'hote'] as RoleFilter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'Toutes' : f === 'voyageur' ? 'Voyageurs' : 'Hôtes'}
                </button>
              ))}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={downloadCsv}>
              Exporter en CSV
            </button>
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="host-table">
              <thead>
                <tr>
                  <th>Testeur</th>
                  <th>Prob.</th>
                  <th>Clarté</th>
                  <th>Freins</th>
                  <th>Bloqué / agacé</th>
                  <th>Une chose à changer</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="host-name">
                        {r.contact?.name ?? 'Anonyme'} · {ROLE_LABELS[r.role]}
                      </div>
                      {r.contact?.email && (
                        <div className="host-email">{r.contact.email}</div>
                      )}
                      <div className="host-email">
                        {DATE_FORMAT.format(new Date(r.updated_at))}
                        {r.app_version ? ` · v${r.app_version}` : ''}
                        {r.has_booked ? ' · a réservé' : ''}
                        {r.has_published ? ' · a publié' : ''}
                      </div>
                    </td>
                    <td>{r.answers.q1_likelihood}</td>
                    <td>{r.answers.q2_price_clarity}</td>
                    <td className="survey-text">
                      {r.answers.q3_barriers.choices.map(optionLabel).join(' · ')}
                      {r.answers.q3_barriers.other && (
                        <div className="host-email">« {r.answers.q3_barriers.other} »</div>
                      )}
                    </td>
                    <td className="survey-text">
                      {r.answers.q4_friction ?? <span className="host-type-missing">—</span>}
                    </td>
                    <td className="survey-text">
                      {r.answers.q5_one_change ?? <span className="host-type-missing">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div className="load-more-row">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={loading}
                onClick={() => void load(cursor)}
              >
                {loading ? 'Chargement…' : 'Charger plus'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
